// AGC: Admin Panel - Meteorologia Configuration Page
// AGC: Configure weather model parameters: spray thresholds, health scores, caching

import React, { useState } from 'react'
import { useTranslation } from '../../../hooks'
import { MetricCard } from './MetricCard'
import { SectionCard } from './SectionCard'
import { EditableSlider } from './EditableSlider'
import { WeightDistribution } from './WeightDistribution'
import { ThresholdEditor } from './ThresholdEditor'
import { FormulaPreview } from './FormulaPreview'
import { DEFAULT_METEOROLOGIA_CONFIG } from '../../../types/admin/models'
import type { MeteorologiaConfig as MeteorologiaConfigType, SprayThresholds } from '../../../types/admin/models'

// AGC: Labels for health score weight factors
const HEALTH_WEIGHT_LABELS: Record<string, string> = {
  precipitation: '💧 Precipitação',
  temperature: '🌡️ Temperatura',
  radiation: '☀️ Radiação',
}

// AGC: Colors for health score weight visualization
const HEALTH_COLORS = ['#3b82f6', '#22c55e', '#f59e0b']

/**
 * AGC: MeteorologiaConfig - Meteorology model configuration page
 * AGC: Allows administrators to configure:
 * AGC: - Spray suitability thresholds (wind, temp, humidity, delta-T, rain)
 * AGC: - Health score weights and thresholds
 * AGC: - Cache settings for weather data
 */
export const MeteorologiaConfig: React.FC = () => {
  const { t } = useTranslation()
  
  // AGC: Configuration state with default values
  const [config, setConfig] = useState<MeteorologiaConfigType>(DEFAULT_METEOROLOGIA_CONFIG)
  const [hasChanges, setHasChanges] = useState(false)
  const [saving, setSaving] = useState(false)

  // AGC: Update spray threshold helper
  const updateSpray = (
    category: keyof SprayThresholds, 
    field: string, 
    value: number
  ) => {
    setConfig(prev => ({
      ...prev,
      spray: {
        ...prev.spray,
        [category]: {
          ...prev.spray[category],
          [field]: value,
        },
      },
    }))
    setHasChanges(true)
  }

  // AGC: Update health score weight
  const updateHealthWeight = (key: string, value: number) => {
    setConfig(prev => ({
      ...prev,
      healthScore: {
        ...prev.healthScore,
        weights: {
          ...prev.healthScore.weights,
          [key]: value,
        },
      },
    }))
    setHasChanges(true)
  }

  // AGC: Update health score threshold
  const updateThreshold = (index: number, field: 'min' | 'label', value: number | string) => {
    const newThresholds = [...config.healthScore.thresholds]
    newThresholds[index] = { ...newThresholds[index], [field]: value }
    setConfig(prev => ({
      ...prev,
      healthScore: {
        ...prev.healthScore,
        thresholds: newThresholds,
      },
    }))
    setHasChanges(true)
  }

  // AGC: Update cache setting
  const updateCache = (field: string, value: number | boolean) => {
    setConfig(prev => ({
      ...prev,
      cache: { ...prev.cache, [field]: value },
    }))
    setHasChanges(true)
  }

  // AGC: Save configuration
  const handleSave = async () => {
    setSaving(true)
    try {
      // TODO: AGC: Implement API call to save config to backend/Firestore
      // await saveMeteorologiaConfig(config)
      console.log('AGC: Saving meteorologia config:', config)
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API
      setConfig(prev => ({
        ...prev,
        updatedAt: new Date().toISOString(),
        updatedBy: 'admin', // AGC: Should get from auth context
      }))
      setHasChanges(false)
      alert('Configurações salvas com sucesso!')
    } catch (error) {
      console.error('AGC: Error saving meteorologia config:', error)
      alert('Erro ao salvar configurações')
    } finally {
      setSaving(false)
    }
  }

  // AGC: Reset to defaults
  const handleReset = () => {
    if (confirm('Resetar todas as configurações para os valores padrão?')) {
      setConfig(DEFAULT_METEOROLOGIA_CONFIG)
      setHasChanges(true)
    }
  }

  // AGC: Generate formula display string
  const formula = `overallScore = (precipitationScore × ${config.healthScore.weights.precipitation.toFixed(2)}) + 
              (temperatureScore × ${config.healthScore.weights.temperature.toFixed(2)}) + 
              (radiationScore × ${config.healthScore.weights.radiation.toFixed(2)})`

  return (
    <div className="pb-24">
      {/* AGC: Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200 flex items-center gap-3">
          <span className="text-3xl">🌦️</span>
          {t('admin.models.meteorologia') || 'Modelos › Meteorologia'}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {t('admin.models.meteorologiaDescription') || 'Configure os parâmetros dos algoritmos meteorológicos e de pulverização'}
        </p>
      </div>

      {/* AGC: Overview metrics */}
      <div className="grid grid-cols-5 gap-5 mb-8">
        <MetricCard icon="🌡️" value="5" label="Variáveis Climáticas" color="#3b82f6" />
        <MetricCard icon="⚙️" value="13" label="Parâmetros Editáveis" color="#22c55e" />
        <MetricCard icon="🎯" value="4" label="Níveis de Alerta" color="#eab308" />
        <MetricCard 
          icon="💧" 
          value={`${(config.healthScore.weights.precipitation * 100).toFixed(0)}%`} 
          label="Peso Precipitação" 
          color="#3b82f6" 
        />
        <MetricCard icon="✅" value="Ativo" label="Status do Modelo" color="#22c55e" />
      </div>

      {/* AGC: Spray Suitability Section */}
      <SectionCard
        title="Aptidão para Pulverização (Spray Suitability)"
        icon="💧"
        description="Configure os umbrais ideais para aplicação de defensivos baseado em padrões internacionais"
        collapsible
        defaultExpanded={true}
      >
        <div className="grid grid-cols-2 gap-8">
          {/* AGC: Wind thresholds */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
              <span className="text-lg">🌬️</span> Vento
            </h4>
            <EditableSlider
              label="Mínimo Ideal"
              value={config.spray.wind.min}
              min={0}
              max={10}
              unit="km/h"
              onChange={(v) => updateSpray('wind', 'min', v)}
              description="Abaixo disso: risco de deriva por falta de dispersão"
            />
            <EditableSlider
              label="Máximo Ideal"
              value={config.spray.wind.max}
              min={5}
              max={25}
              unit="km/h"
              onChange={(v) => updateSpray('wind', 'max', v)}
              description="Limite superior para aplicação segura"
            />
            <EditableSlider
              label="Crítico (Não Aplicar)"
              value={config.spray.wind.critical}
              min={15}
              max={40}
              unit="km/h"
              onChange={(v) => updateSpray('wind', 'critical', v)}
              description="Acima disso: aplicação proibida"
            />
          </div>

          {/* AGC: Temperature thresholds */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
              <span className="text-lg">🌡️</span> Temperatura
            </h4>
            <EditableSlider
              label="Mínima Ideal"
              value={config.spray.temperature.min}
              min={0}
              max={20}
              unit="°C"
              onChange={(v) => updateSpray('temperature', 'min', v)}
            />
            <EditableSlider
              label="Máxima Ideal"
              value={config.spray.temperature.max}
              min={20}
              max={40}
              unit="°C"
              onChange={(v) => updateSpray('temperature', 'max', v)}
            />
            <EditableSlider
              label="Crítica (Não Aplicar)"
              value={config.spray.temperature.critical}
              min={30}
              max={45}
              unit="°C"
              onChange={(v) => updateSpray('temperature', 'critical', v)}
            />
          </div>

          {/* AGC: Humidity thresholds */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
              <span className="text-lg">💨</span> Umidade
            </h4>
            <EditableSlider
              label="Mínima Ideal"
              value={config.spray.humidity.min}
              min={30}
              max={70}
              unit="%"
              onChange={(v) => updateSpray('humidity', 'min', v)}
              description="Abaixo: risco de evaporação das gotas"
            />
            <EditableSlider
              label="Máxima Ideal"
              value={config.spray.humidity.max}
              min={80}
              max={100}
              unit="%"
              onChange={(v) => updateSpray('humidity', 'max', v)}
              description="Acima: risco de escorrimento nas folhas"
            />
          </div>

          {/* AGC: Delta-T thresholds */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
              <span className="text-lg">📐</span> Delta-T (Bulbo Seco - Bulbo Úmido)
            </h4>
            <EditableSlider
              label="Mínimo Ideal"
              value={config.spray.deltaT.min}
              min={0}
              max={5}
              unit="°C"
              onChange={(v) => updateSpray('deltaT', 'min', v)}
              description="Abaixo: inversão térmica, gotas não dispersam"
            />
            <EditableSlider
              label="Máximo Ideal"
              value={config.spray.deltaT.max}
              min={5}
              max={12}
              unit="°C"
              onChange={(v) => updateSpray('deltaT', 'max', v)}
              description="Acima: evaporação excessiva"
            />
            <EditableSlider
              label="Crítico"
              value={config.spray.deltaT.critical}
              min={8}
              max={15}
              unit="°C"
              onChange={(v) => updateSpray('deltaT', 'critical', v)}
            />
          </div>

          {/* AGC: Rain forecast thresholds */}
          <div className="col-span-2">
            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
              <span className="text-lg">🌧️</span> Previsão de Chuva
            </h4>
            <div className="grid grid-cols-2 gap-8">
              <EditableSlider
                label="Horas de Verificação"
                value={config.spray.rain.checkHours}
                min={1}
                max={12}
                unit="h"
                onChange={(v) => updateSpray('rain', 'checkHours', v)}
                description="Janela de tempo para verificar previsão de chuva"
              />
              <EditableSlider
                label="Probabilidade de Alerta"
                value={config.spray.rain.probabilityThreshold}
                min={20}
                max={80}
                unit="%"
                onChange={(v) => updateSpray('rain', 'probabilityThreshold', v)}
                description="Acima disso: alertar usuário sobre risco de chuva"
              />
            </div>
          </div>
        </div>
      </SectionCard>

      {/* AGC: Health Score Section */}
      <SectionCard
        title="Health Score (Saúde Meteorológica do Lote)"
        icon="📊"
        description="Ponderação dos fatores para cálculo do score de saúde baseado em metodologia ZARC"
        collapsible
        defaultExpanded={true}
      >
        <div className="grid grid-cols-2 gap-8">
          <WeightDistribution
            weights={config.healthScore.weights}
            labels={HEALTH_WEIGHT_LABELS}
            colors={HEALTH_COLORS}
            onChange={updateHealthWeight}
          />
          <ThresholdEditor
            thresholds={config.healthScore.thresholds}
            onChange={updateThreshold}
          />
        </div>

        <FormulaPreview
          title="Fórmula Atual"
          formula={formula}
        />
      </SectionCard>

      {/* AGC: Cache Settings Section */}
      <SectionCard
        title="Cache e Performance"
        icon="⚡"
        description="Configurações de cache para otimização de requisições à API meteorológica"
        collapsible
        defaultExpanded={false}
      >
        <div className="grid grid-cols-2 gap-8">
          <EditableSlider
            label="Duração do Cache"
            value={config.cache.durationMinutes}
            min={5}
            max={120}
            unit="min"
            onChange={(v) => updateCache('durationMinutes', v)}
            description="Tempo que os dados meteorológicos ficam em cache local"
          />
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-3">
              Fallback Offline
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => updateCache('offlineFallback', true)}
                className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                  config.cache.offlineFallback
                    ? 'bg-green-500 text-white'
                    : 'bg-slate-100 dark:bg-white/[0.04] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                }`}
              >
                ✓ Ativado
              </button>
              <button
                onClick={() => updateCache('offlineFallback', false)}
                className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                  !config.cache.offlineFallback
                    ? 'bg-red-500 text-white'
                    : 'bg-slate-100 dark:bg-white/[0.04] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                }`}
              >
                ✗ Desativado
              </button>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              Se ativado, usa último dado em cache quando estiver offline
            </p>
          </div>
        </div>

        {/* AGC: Technical info */}
        <div className="mt-6 p-4 bg-info-50 dark:bg-blue-500/15 border border-slate-200 dark:border-white/5 rounded-xl">
          <h4 className="text-sm font-semibold text-info-700 dark:text-blue-400 mb-2">ℹ️ Informação Técnica</h4>
          <p className="text-sm text-info-700 dark:text-blue-400">
            Os dados meteorológicos são obtidos via Open-Meteo API e processados localmente.
            O cache reduz chamadas à API e melhora a performance em áreas com conexão instável.
          </p>
        </div>
      </SectionCard>

      {/* AGC: Floating save bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-admin-card-dark border-t border-slate-200 dark:border-white/5 px-4 pt-4 pb-safe-4 flex justify-between items-center shadow-lg z-[90]">
          <div className="flex items-center gap-2 text-amber-600">
            <span className="text-lg">⚠️</span>
            <span className="text-sm font-medium">Você tem alterações não salvas</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="px-5 py-2.5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/[0.02] flex items-center gap-2 font-medium"
            >
              🔄 Resetar
            </button>
            <button
              onClick={() => setHasChanges(false)}
              className="px-5 py-2.5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/[0.02] flex items-center gap-2"
            >
              ✕ Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-brand-primary text-white dark:bg-brand-light dark:text-[#052e22] rounded-lg hover:bg-brand-dark dark:hover:opacity-90 flex items-center gap-2 font-medium disabled:opacity-50"
            >
              💾 {saving ? 'Salvando...' : 'Salvar Configurações'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default MeteorologiaConfig


